export interface PrintReportParams {
  companyName: string;
  branchName: string;
  branchAddress?: string;
  period: string;
  totalReviews: number;
  overallAverage: number;
  rows: { userId: string; empNo: string; name: string; averageRating: number; totalReviews: number }[];
}

export function printRatingReport(params: PrintReportParams): void {
  const { companyName, branchName, branchAddress, period, totalReviews, overallAverage, rows } = params;

  const tableRows = rows.length === 0
    ? `<tr><td colspan="5" style="text-align:center;color:#888;padding:24px;">No reviews for this period.</td></tr>`
    : rows.map((r, i) => `
        <tr class="${i % 2 === 0 ? 'even' : 'odd'}">
          <td>${i + 1}</td>
          <td style="font-family:monospace;font-size:0.9em;">${r.empNo || '—'}</td>
          <td><strong>${r.name}</strong></td>
          <td style="text-align:center;">${r.totalReviews}</td>
          <td style="text-align:center;font-weight:700;color:#0a2d6b;">${r.averageRating.toFixed(2)} ★</td>
        </tr>`).join('');

  const starsHtml = (avg: number) => {
    const full = Math.floor(avg);
    const half = avg - full >= 0.5 ? 1 : 0;
    const empty = 5 - full - half;
    return '★'.repeat(full) + (half ? '½' : '') + '☆'.repeat(empty);
  };

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Rating Report — ${branchName}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: "Segoe UI", Arial, sans-serif;
      font-size: 14px;
      color: #1a1a2e;
      background: #f4f7fb;
      padding: 32px;
    }

    .page {
      max-width: 860px;
      margin: 0 auto;
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 4px 24px rgba(0,0,0,0.10);
      overflow: hidden;
    }

    /* ── Header banner ── */
    .banner {
      background: linear-gradient(135deg, #0a2d6b 0%, #001a47 100%);
      color: #fff;
      padding: 28px 32px 24px;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 16px;
    }
    .banner-left h1 {
      font-size: 1.4em;
      font-weight: 800;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      margin-bottom: 14px;
    }
    .banner-left h1 span { color: #FFD54F; }
    .meta-row { display: flex; gap: 6px; margin-bottom: 6px; font-size: 0.88em; }
    .meta-label { color: rgba(255,255,255,0.6); min-width: 80px; }
    .meta-value { color: #fff; font-weight: 600; }

    /* ── Summary cards ── */
    .summary {
      display: flex;
      gap: 0;
      border-bottom: 1px solid #e8edf5;
    }
    .summary-card {
      flex: 1;
      padding: 20px 32px;
      border-right: 1px solid #e8edf5;
      text-align: center;
    }
    .summary-card:last-child { border-right: none; }
    .summary-number {
      font-size: 2em;
      font-weight: 800;
      color: #0a2d6b;
      line-height: 1;
      margin-bottom: 4px;
    }
    .summary-stars {
      font-size: 1.3em;
      color: #FFD54F;
      letter-spacing: 2px;
      margin-bottom: 4px;
    }
    .summary-label { font-size: 0.78em; color: #888; text-transform: uppercase; letter-spacing: 0.08em; }

    /* ── Table ── */
    .table-wrap { padding: 24px 32px 32px; }
    .table-title {
      font-size: 0.78em;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      color: #888;
      margin-bottom: 12px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.92em;
    }
    thead tr {
      background: #0a2d6b;
      color: #fff;
    }
    thead th {
      padding: 11px 14px;
      text-align: left;
      font-weight: 700;
      font-size: 0.82em;
      letter-spacing: 0.06em;
      text-transform: uppercase;
    }
    thead th:nth-child(4),
    thead th:nth-child(5) { text-align: center; }
    tbody td { padding: 10px 14px; vertical-align: middle; }
    tr.even { background: #f8faff; }
    tr.odd  { background: #fff; }
    tbody tr:hover { background: #eef2ff; }

    /* ── Footer ── */
    .footer {
      text-align: center;
      padding: 14px;
      font-size: 0.75em;
      color: #aaa;
      border-top: 1px solid #e8edf5;
    }

    /* ── Print overrides ── */
    @media print {
      body { background: #fff; padding: 0; }
      .page { box-shadow: none; border-radius: 0; max-width: 100%; }
      .banner { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      thead tr { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      tr.even { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <div class="page">

    <div class="banner">
      <div class="banner-left">
        <h1>${companyName || '—'} — Staff Rating Report</h1>
         
        <div class="meta-row"><span class="meta-label">Branch</span><span class="meta-value">${branchName}${branchAddress ? ` &mdash; ${branchAddress}` : ''}</span></div>
        <div class="meta-row"><span class="meta-label">Period</span><span class="meta-value">${period}</span></div>
        <div class="meta-row"><span class="meta-label">Generated</span><span class="meta-value">${new Date().toLocaleString()}</span></div>
      </div>
    </div>

    <div class="summary">
      <div class="summary-card">
        <div class="summary-number">${totalReviews}</div>
        <div class="summary-label">Total Reviews</div>
      </div>
      <div class="summary-card">
        <div class="summary-number">${overallAverage > 0 ? overallAverage.toFixed(2) : '—'}</div>
        <div class="summary-stars">${overallAverage > 0 ? starsHtml(overallAverage) : ''}</div>
        <div class="summary-label">Overall Average</div>
      </div>
      <div class="summary-card">
        <div class="summary-number">${rows.length}</div>
        <div class="summary-label">Employees Reviewed</div>
      </div>
    </div>

    <div class="table-wrap">
      <div class="table-title">Employee Performance Summary &nbsp;·&nbsp; Sorted by Average Rating</div>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Emp No</th>
            <th>Name</th>
            <th>Reviews</th>
            <th>Avg Rating</th>
          </tr>
        </thead>
        <tbody>${tableRows}</tbody>
      </table>
    </div>

    <div class="footer">Generated by RankMe &nbsp;·&nbsp; www.rankme.lk</div>
  </div>

  <script>
    window.onload = function () { window.print(); window.close(); }
  </script>
</body>
</html>`;

  const popup = window.open('', '_blank');
  if (popup) {
    popup.document.open();
    popup.document.write(html);
    popup.document.close();
  }
}
